import React, { useContext } from 'react';
import { useThree } from '@react-three/fiber';
import * as THREE from 'three';
import { SCENE_DIMENSIONS, LIGHTING_CONFIG } from '../config/sceneConfig';
import { useMobileDetection } from '../../../../hooks/useMobileDetection';

export function Lighting({ roomType = 'classic' }) {
    const { isMobile } = useMobileDetection();
    const { width, depth } = SCENE_DIMENSIONS;
    const { ambient, spotlights, wallWash } = LIGHTING_CONFIG;
    const { scene } = useThree();
    
    // Adjust lighting based on room type
    const isSpacious = roomType === 'spacious';
    
    // Dark theme lighting for spacious room
    const spaciousLighting = {
        ambient: {
            intensity: 0.2,
            color: "#2a2a4a" // Dark blue tint
        },
        spotlights: {
            intensity: 0.6,
            color: "#e0e0ff", // Slightly blue-tinted light
            height: 9 // Higher ceiling
        },
        wallWash: {
            intensity: 0.4,
            color: "#4a4a6a", // Dark blue gray
            height: 5
        }
    };
    
    // Use appropriate lighting config based on room type
    const lighting = isSpacious ? spaciousLighting : {
        ambient: {
            intensity: 0.2,
            color: "#fff0e0"  // Warm cream color
        },
        spotlights: {
            intensity: 0.3,
            color: "#ffe8c0", // Warm amber light
            height: 7.5
        },
        wallWash: {
            intensity: 0.2,
            color: "#ffecd0", // Soft warm glow
            height: 3
        }
    };

    // Set scene background color if using dark theme
    if (isSpacious) {
        scene.background = new THREE.Color("#1a1a1a");
    }

    // Calculate grid positions for spotlights
    // Adjust spacing for spacious room
    const gridSpacingX = isSpacious ? width/2.5 : width/3;
    const gridSpacingZ = isSpacious ? depth/2.5 : depth/3;
    
    // Reduced number of lights - just using key positions
    const gridPositions = [
        [0, 0], // Center
        [0, -gridSpacingZ], // Back
        [-gridSpacingX, 0], // Left
        [gridSpacingX, 0], // Right
    ];

    return (
        <group>
            {/* Base ambient light */}
            <ambientLight 
                intensity={lighting.ambient.intensity} 
                color={lighting.ambient.color} 
            />

            {/* Grid of spotlights */}
            {gridPositions.map(([x, z], index) => (
                <group key={`light-${index}`}>
                    {/* Light fixture cone */}
                    <mesh position={[x, lighting.spotlights.height + 0.4, z]} rotation={[Math.PI, 0, 0]}>
                        <coneGeometry args={[0.2, 0.3, 32]} />
                        <meshStandardMaterial color={isSpacious ? "#4a4a4a" : "#1a1a1a"} />
                    </mesh>
                    
                    {/* Light fixture base */}
                    <mesh position={[x, lighting.spotlights.height + 0.25, z]}>
                        <cylinderGeometry args={[0.15, 0.15, 0.1, 32]} />
                        <meshStandardMaterial 
                            emissive={isSpacious ? "#a0a0ff" : "#ffe0c0"} 
                            emissiveIntensity={isSpacious ? 0.6 : 0.5} 
                        />
                    </mesh>
                    
                    {/* Actual light source */}
                    <pointLight 
                        position={[x, lighting.spotlights.height, z]}
                        intensity={lighting.spotlights.intensity * 0.7}
                        color={lighting.spotlights.color}
                        distance={isSpacious ? 25 : 15}
                        decay={2.5}
                    />
                </group>
            ))}

            {/* Wall wash lights - reduced intensity */}
            <pointLight 
                position={[-width/2, lighting.wallWash.height, 0]} 
                intensity={lighting.wallWash.intensity * 0.6} 
                color={lighting.wallWash.color} 
            />
            <pointLight 
                position={[width/2, lighting.wallWash.height, 0]} 
                intensity={lighting.wallWash.intensity * 0.6} 
                color={lighting.wallWash.color} 
            />
            
            {/* Add additional directional light for spacious room to create dramatic shadows */}
            {isSpacious && (
                <directionalLight
                    position={[0, 8, 10]}
                    intensity={0.4}
                    color="#c0c0ff"
                    castShadow={!isMobile} // Disable shadows on mobile
                />
            )}
        </group>
    );
} 